import Tabs from "components/lib/Shared/Tabs";
import BeneficialOwnership from "components/PLCTABS/ApplicationReviewTab/BeneficialOwnership";
import { Roles_Permissions_Constants } from "config/roles-permissions";
import { useEffect, useMemo, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useSelector } from "react-redux";
import CompanySummary from "../BusinessDetails/CompanySummary";
import RegisteredAddress from "../BusinessDetails/RegisteredAddress";
import ShareInformation from "../BusinessDetails/ShareInformation";
import StakeholderDetails from "../BusinessDetails/StakeholderDetails";
import { filterCompanyVersions } from "./filterCompanyVersions";

const History = () => {
  const {
    beneficialOwners,
    groupedBeneficialOwners,
    dashboardPermissions,
    designation,
    company,
    companyOfficials,
  } = useSelector((state) => state.companyDashboardSlice);

  const [activeYearIndex, setActiveYearIndex] = useState(null);
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const [selectedTab, setSelectedTab] = useState(null);
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);

  const viewRestrictedData = dashboardPermissions.some(
    (permission) =>
      permission.name ===
      Roles_Permissions_Constants.DASHBOARD_PERMISSIONS.VIEW_RESTRICTED_DATA
  );

  const isHistory = true;

  const generateYearTabs = () => {
    if (!company?.registrationDate) return [];

    const registrationDate = new Date(company.registrationDate);
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const registrationYear = registrationDate.getFullYear();
    const isJan1Registration =
      registrationDate.getMonth() === 0 && registrationDate.getDate() === 1;
    const tabs = [];

    // Determine the start date for the Current tab
    let currentStartYear = currentYear;
    const anniversaryThisYear = isJan1Registration
      ? new Date(currentYear, 0, 1)
      : new Date(
          currentYear,
          registrationDate.getMonth(),
          registrationDate.getDate()
        );

    // If the anniversary this year is after the current date, use the previous year's anniversary
    if (anniversaryThisYear > currentDate) {
      currentStartYear = currentYear - 1;
    }

    const currentStartDate = isJan1Registration
      ? new Date(currentStartYear, 0, 1)
      : new Date(
          currentStartYear,
          registrationDate.getMonth(),
          registrationDate.getDate()
        );

    const { hasChange } = filterCompanyVersions({
      dateFrom: currentStartDate,
      dateTo: currentDate,
      company,
      companyOfficials: companyOfficials.items,
    });
    tabs.unshift({
      id: "current",
      label: "Current",
      startDate: currentStartDate,
      endDate: currentDate,
      hasChange,
    });

    // Generate tabs for completed return periods in descending order
    for (let year = currentYear - 1; year >= registrationYear; year--) {
      const startDate = isJan1Registration
        ? new Date(year, 0, 1) // January 1
        : new Date(
            year,
            registrationDate.getMonth(),
            registrationDate.getDate()
          );
      const endDate = isJan1Registration
        ? new Date(year, 11, 31) // December 31
        : new Date(
            year + 1,
            registrationDate.getMonth(),
            registrationDate.getDate() - 1
          );
      if (currentDate >= endDate) {
        const { hasChange } = filterCompanyVersions({
          dateFrom: startDate,
          dateTo: endDate,
          company,
          companyOfficials: companyOfficials.items,
        });
        tabs.push({
          id: isJan1Registration ? `${year}` : `${year + 1}/${year}`,
          label: isJan1Registration ? `${year}` : `${year + 1}/${year}`,
          startDate,
          endDate,
          hasChange,
        });
      }
    }

    return tabs;
  };

  const yearTabs = useMemo(
    () => generateYearTabs(),
    [company?.registrationDate, company?.history]
  );

  const nestedTabs = useMemo(() => {
    return [
      { id: "companySummary", label: "Summary" },
      { id: "registeredAddress", label: "Registered Address" },
      { id: "shareInformation", label: "Share Information" },
      { id: "stakeholderDetails", label: "Stakeholder Details" },
      viewRestrictedData || designation.includes("Shareholder")
        ? { id: "beneficialOwnership", label: "Beneficial Ownership" }
        : null,
    ].filter(Boolean);
  }, [viewRestrictedData, designation]);

  useEffect(() => {
    if (yearTabs.length > 0 && activeYearIndex === null) {
      setActiveYearIndex(0);
      setSelectedTabIndex(0);
      setSelectedTab(nestedTabs[0]);
      const { startDate, endDate } = yearTabs[0];
      setDateFrom(startDate);
      setDateTo(endDate);
    }
  }, [yearTabs, nestedTabs, activeYearIndex]);

  useEffect(() => {
    if (activeYearIndex !== null && yearTabs[activeYearIndex]) {
      const { startDate, endDate } = yearTabs[activeYearIndex];
      setDateFrom(startDate);
      setDateTo(endDate);
    }
  }, [activeYearIndex, yearTabs]);

  useEffect(() => {
    const tempSelectedTab = nestedTabs[selectedTabIndex];
    if (tempSelectedTab) setSelectedTab(tempSelectedTab);
  }, [nestedTabs, selectedTabIndex]);

  const renderDetails = () => {
    if (activeYearIndex === null) return <div>Loading...</div>;
    if (!company || !company._id) return <div>No company data available</div>;

    const companyId = company._id;
    const { startDate, endDate } = yearTabs[activeYearIndex];

    return (
      <div>
        <div className="flex items-center gap-4 my-6">
          <h2 className="text-lg">Period:</h2>
          <div className="flex flex-row items-center gap-2">
            <label className="text-primary">From</label>
            <DatePicker
              selected={dateFrom}
              onChange={(date) => setDateFrom(date)}
              dateFormat="dd/MM/yyyy"
              minDate={startDate}
              maxDate={endDate}
              placeholderText="Select start date"
              className="block w-full px-3 py-2 mt-1 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm disabled:bg-slate-50 disabled:text-slate-500 disabled:border-slate-200 disabled:shadow-none disabled:cursor-not-allowed"
            />
          </div>
          <div className="flex flex-row items-center gap-2">
            <label className="text-primary">To </label>
            <DatePicker
              selected={dateTo}
              onChange={(date) => setDateTo(date)}
              dateFormat="dd/MM/yyyy"
              minDate={startDate}
              maxDate={endDate}
              placeholderText="Select end date"
              className="block w-full px-3 py-2 mt-1 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm disabled:bg-slate-50 disabled:text-slate-500 disabled:border-slate-200 disabled:shadow-none disabled:cursor-not-allowed"
            />
          </div>
        </div>

        <Tabs
          tabs={nestedTabs}
          selectedTabIndex={selectedTabIndex}
          setSelectedTabIndex={setSelectedTabIndex}
          showTooltip
        />

        {selectedTab?.id === "companySummary" && (
          <CompanySummary
            isViewMode={true}
            dateFrom={dateFrom}
            dateTo={dateTo}
            isHistory={isHistory}
          />
        )}
        {selectedTab?.id === "registeredAddress" && (
          <RegisteredAddress
            dateFrom={dateFrom}
            dateTo={dateTo}
            isHistory={isHistory}
          />
        )}
        {selectedTab?.id === "shareInformation" && (
          <ShareInformation
            disabledRightsEditor
            dateFrom={dateFrom}
            dateTo={dateTo}
          />
        )}
        {selectedTab?.id === "stakeholderDetails" && (
          <StakeholderDetails
            companyId={companyId}
            dateFrom={dateFrom}
            dateTo={dateTo}
            isHistory={isHistory}
          />
        )}
        {selectedTab?.id === "beneficialOwnership" && (
          <BeneficialOwnership
            beneficialOwners={beneficialOwners.items}
            groupedBeneficialOwners={groupedBeneficialOwners}
            hideReviewWorkflow
            renderShortTypeOfControl
            showDocumentLabel
            dateFrom={dateFrom}
            dateTo={dateTo}
          />
        )}
      </div>
    );
  };

  return (
    <div>
      <h2 className="text-lg font-medium mb-4">Company History</h2>
      <p className="text-sm text-sflPrimary mb-6">
        View the history of your company, including changes to its registered
        address, share information, and beneficial ownership.
      </p>
      {yearTabs.length > 0 ? (
        <>
          <Tabs
            tabs={yearTabs}
            selectedTabIndex={activeYearIndex}
            setSelectedTabIndex={setActiveYearIndex}
            showTooltip
            historyTabsClassName={(tab, index) =>
              `py-2 px-4 text-sm mx-4 font-medium cursor-pointer relative ${
                index === activeYearIndex
                  ? "bg-white text-gray-300"
                  : "text-gray-500"
              }`
            }
          />
          {renderDetails()}
        </>
      ) : (
        <div>No return periods available yet</div>
      )}
    </div>
  );
};

export default History;
